import { compose, filter, get, isEmpty, map, sortBy } from 'lodash/fp';
import { formValueSelector } from 'redux-form';
import { createSelector } from 'reselect';
import { getStringProp, getStringArrayProp, getProp } from '@mews/fp';

import {
    composeProps,
    createAsyncMetaSelectors,
    createPropsExtender,
    DateTimeService,
    DateTimeUtcService,
} from '@mews/framework';

import { RouteName } from '@mews-commander/enums';
import { ResolveRoute } from '@mews/routing';
import { Task } from '@mews-commander/data-access';
import { TemporalTaskState } from '@mews-commander/legacy-data-structures';
import { sortByAscending, sortByDescending, getNowUtc, getAppNowUtcProp } from '@mews-commander/utils';
import { getProduct, isProductPostedByTimeUnit } from '@mews-commander/legacy-entities/src/ducks/products';
import {
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getCurrentEnterpriseEditableHistoryIntervalMilliseconds,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getCurrentEnterpriseTasksByServiceOrderIds } from '@mews-commander/legacy-entities/src/ducks/tasks';
import { getEndOfTodayUtc } from '@mews-commander/legacy-entities/src/selectors/session/dateTime';
import { mapProductOptions } from '@mews-commander/legacy-entities/src/utils/mapProductOptions';
import { NAME as tasksModuleName } from '../ducks/tasks';
import { moduleSelector } from '../module';

export const getProductOptions = (isMultiTaxEnvironment: $TSFixMe, prices: $TSFixMe) =>
    compose(mapProductOptions(isMultiTaxEnvironment, prices), sortBy('ordering'), filter('options.offerToEmployee'));

const getSelectedProductId = (state: $TSFixMe, { form }: $TSFixMe) => formValueSelector(form)(state, 'productId');
const getSelectedProduct = composeProps(getProduct, createPropsExtender('id', getSelectedProductId));
export const hideConsumedField = (state: $TSFixMe, { form }: $TSFixMe) =>
    // @ts-expect-error $TSFixMeError: Object is possibly 'undefined'.
    compose((product) => isEmpty(product) || isProductPostedByTimeUnit(state, { id: product.id }), getSelectedProduct)(
        state,
        { form }
    );

export const getMinEditableUtc = createSelector(
    getAppNowUtcProp,
    getCurrentEnterpriseEditableHistoryIntervalMilliseconds,
    (appNowUtc, editableHistoryIntervalMillies) => {
        const editableHistoryIntervalSeconds = (editableHistoryIntervalMillies as $TSFixMe) / 1000;

        const { value } = DateTimeUtcService.addSeconds(appNowUtc, -editableHistoryIntervalSeconds);
        return value;
    }
);

const getTasksState = createSelector(moduleSelector, get(tasksModuleName));
const getTasksMetaState = compose(get('meta'), getTasksState);

export const [areTasksFetching, hasTasksFailed] = createAsyncMetaSelectors(getTasksMetaState);

const getSortedTasksByServiceOrderIds = compose(
    sortByDescending<Task>('closedUtc'),
    sortByAscending<Task>('deadlineUtc'),
    getCurrentEnterpriseTasksByServiceOrderIds
);

const getTaskTemporalState = ({ deadlineUtc, closedUtc, nowUtc, endOfTodayUtc }: $TSFixMe) => {
    if (closedUtc) {
        return TemporalTaskState.Closed;
    }

    if (deadlineUtc <= nowUtc) {
        return TemporalTaskState.Missed;
    }

    if (deadlineUtc > endOfTodayUtc) {
        return TemporalTaskState.Future;
    }

    return TemporalTaskState.Pending;
};

export const getSortedMappedTasksByServiceOrderIds = createSelector(
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getSortedTasksByServiceOrderIds,
    getEndOfTodayUtc,
    (timezone: string, tasks, endOfTodayUtc) =>
        map((task) => {
            const nowUtc = getNowUtc();
            // @ts-expect-error $TSFixMeError: Property 'deadlineUtc' does not exist on type '{}'.
            const { deadlineUtc, closedUtc } = task;

            const temporalState = getTaskTemporalState({ deadlineUtc, closedUtc, nowUtc, endOfTodayUtc });
            const deadlineDateTime = DateTimeService.fromUtcString(deadlineUtc, timezone);

            return {
                ...task,
                temporalState,
                deadlineDateTime,
            };
        }, tasks)
);

export const getBillDetailUrl = createSelector(
    getProp<ResolveRoute>()('resolveRoute'),
    getStringProp('id'),
    (resolveRoute, id) => resolveRoute(RouteName.BillDetail, { id })
);

export const getReservationMembershipFileUrl = createSelector(
    getProp<ResolveRoute>()('resolveRoute'),
    getStringProp('groupId'),
    (resolveRoute, groupId) =>
        resolveRoute(RouteName.ReservationMembershipExport, { groupId, query: { Layout: 'Empty' } })
);

export const getRegistrationCardUrl = createSelector(
    getProp<ResolveRoute>()('resolveRoute'),
    getStringArrayProp('reservationIds'),
    (resolveRoute, reservationIds) =>
        resolveRoute(RouteName.CustomerReservationRegistrationCards, {
            query: { ReservationIds: reservationIds, ForPrint: 'True' },
        })
);

export * from './reservationModuleState';
export * from './summary';
export * from './dateGroups';
